import { Flame, Zap } from "lucide-react";

import { cn } from "@/lib/utils";

import type { MarketCommodity } from "@/types/market-intelligence";

const OPTIONS: ReadonlyArray<{ value: MarketCommodity; label: string; icon: typeof Zap; on: string; segment: string }> = [
  {
    value: "power",
    label: "Electric",
    icon: Zap,
    on: "border-primary-mid bg-primary-dim text-primary",
    segment: "bg-primary-dim text-primary",
  },
  {
    value: "gas",
    label: "Gas",
    icon: Flame,
    on: "border-warning-mid bg-warning-dim text-warning",
    segment: "bg-warning-dim text-warning",
  },
];

/**
 * Electric | Gas, as Market Intelligence's sidebar draws it
 * (`MarketCommodityToggle`) and as the report point pickers' quick filters
 * reuse it (`PointQuickFilters`).
 *
 * **Two plain outlined pills, not a bordered `ToggleGroup`.** In the sidebar
 * it sits between the module switcher (a bordered card) and the region picker
 * (an accent-stripe card). A first version filled both segments solid —
 * Electric in teal, Gas in gold — and between those two cards it read as a
 * third loud block; gold especially, at full saturation, pulled the eye before
 * anything else. Both pills sit at rest as a thin border with muted text; only
 * the active one picks up a soft tinted fill (`bg-primary-dim` /
 * `bg-warning-dim`, the dim-fill weight `ScopePicker`'s selected state uses).
 *
 * **Colour is commodity semantics, not a generic "active" state.** Electric
 * keeps `--primary`, the tone every other selected pill in the app uses; Gas
 * keeps `--warning`, the gold-amber `CommodityToggle` and `CommodityBadge`
 * (via `--commodity-gas`) use for gas, at dim-fill weight.
 *
 * **`segmented`** is the same switch for a dropdown's filter band
 * (`PointQuickFilters`): one bordered track with the two halves inside, so
 * it reads as one control beside the borderless region chips rather than two
 * more outlined boxes.
 *
 * `"power"` is the DATA value; "Electric" is what the label says (see
 * `paths.ts`). A commodity outside `available` is disabled: a picker whose
 * points are all one commodity still shows the switch, and says why the other
 * side is empty.
 */
export function CommoditySwitch({
  value,
  onChange,
  variant = "pills",
  available,
  unavailableHint,
}: {
  value: MarketCommodity;
  onChange: (next: MarketCommodity) => void;
  /** `pills` in the sidebar; `segmented` in a picker's dropdown. */
  variant?: "pills" | "segmented";
  /** The commodities with anything to show; every one when omitted. */
  available?: ReadonlySet<MarketCommodity>;
  /** The disabled pill's tooltip. */
  unavailableHint?: (commodity: MarketCommodity) => string;
}) {
  const segmented = variant === "segmented";
  return (
    <div
      role="radiogroup"
      aria-label="Commodity"
      className={
        segmented
          ? "grid grid-cols-2 gap-0.5 rounded-md border border-border bg-card p-0.5"
          : "flex items-center gap-1.5"
      }
    >
      {OPTIONS.map(({ value: commodity, label, icon: Icon, on, segment }) => {
        const disabled = available !== undefined && !available.has(commodity);
        return (
          <button
            key={commodity}
            type="button"
            role="radio"
            aria-checked={value === commodity}
            disabled={disabled}
            title={disabled ? unavailableHint?.(commodity) : undefined}
            onClick={() => onChange(commodity)}
            className={cn(
              "flex items-center justify-center gap-1.5 font-medium transition-colors",
              "disabled:cursor-not-allowed disabled:opacity-40",
              segmented
                ? cn(
                    "rounded px-2 py-1 text-xs",
                    value === commodity ? segment : "text-muted-foreground enabled:hover:text-foreground",
                  )
                : cn(
                    "flex-1 rounded-md border px-2 py-1.5 text-sm",
                    value === commodity
                      ? on
                      : "border-border text-muted-foreground enabled:hover:border-border-strong enabled:hover:text-foreground",
                  ),
            )}
          >
            <Icon size={segmented ? 12 : 14} strokeWidth={1.75} />
            {label}
          </button>
        );
      })}
    </div>
  );
}
